import { createSearchClient, readConfigFromJson, SearchConfig } from '../../../../js/services/meilisearch';

interface SearchHit {
  id?: string | number;
  title?: string;
  type?: string;
  url?: string;
  attributes?: {
    cmsLink?: string;
  };
  images?: {
    dropdown?: string;
    dropdown2x?: string;
  };
  _formatted?: {
    title?: string;
  };
}

type SearchForm = HTMLFormElement & {
  dataset: DOMStringMap & {
    searchConfig?: string;
    searchMinLength?: string;
    searchLimit?: string;
    searchDebounceMs?: string;
    searchSeeAllLabel?: string;
    searchNoResultsLabel?: string;
    searchErrorLabel?: string;
  };
};

export class HeaderSearchAutocomplete {
  private readonly form: SearchForm;
  private readonly input: HTMLInputElement;
  private readonly list: HTMLDivElement;
  private readonly clearButton: HTMLButtonElement | null;
  private readonly config: SearchConfig;
  private readonly minLength: number;
  private readonly limit: number;
  private readonly debounceMs: number;
  private readonly seeAllLabel: string;
  private readonly noResultsLabel: string;
  private readonly errorLabel: string;

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private activeRequest = 0;
  private highlightedIndex = -1;
  private hits: SearchHit[] = [];

  constructor(form: SearchForm) {
    const input = form.querySelector<HTMLInputElement>('input[type="search"]');
    const list = form.querySelector<HTMLDivElement>('#cms-header-search-suggestions');

    if (!input || !list || !form.dataset.searchConfig) {
      throw new Error('Autocomplete initialization failed. Missing required search elements.');
    }

    this.form = form;
    this.input = input;
    this.list = list;
    this.clearButton = form.querySelector<HTMLButtonElement>('.js-cms-header-search-clear');
    this.config = readConfigFromJson(form.dataset.searchConfig);
    this.minLength = Number(form.dataset.searchMinLength ?? '2');
    this.limit = Number(form.dataset.searchLimit ?? '8');
    this.debounceMs = Number(form.dataset.searchDebounceMs ?? '250');
    this.seeAllLabel = form.dataset.searchSeeAllLabel ?? 'See all results';
    this.noResultsLabel = form.dataset.searchNoResultsLabel ?? 'No results found';
    this.errorLabel = form.dataset.searchErrorLabel ?? 'Search is currently unavailable. Please try again later.';

    this.attachEvents();
  }

  private attachEvents(): void {
    this.input.addEventListener('input', this.onInputChange);
    this.input.addEventListener('keydown', this.onInputKeyDown);
    this.list.addEventListener('mousedown', this.onListMouseDown);
    this.clearButton?.addEventListener('click', this.onClearButtonClick);
    document.addEventListener('mousedown', this.onDocumentMouseDown);

    this.updateClearButtonVisibility();
  }

  private readonly onInputChange = (): void => {
    const query = this.input.value.trim();
    this.updateClearButtonVisibility();

    if (query.length < this.minLength) {
      this.clearSuggestions();
      return;
    }

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.renderLoadingState();
    this.debounceTimer = setTimeout(() => {
      void this.fetchSuggestions(query);
    }, this.debounceMs);
  };

  private readonly onInputKeyDown = (event: KeyboardEvent): void => {
    if (this.hits.length === 0) {
      if (event.key === 'Escape') {
        this.closeSuggestions();
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.highlightedIndex = this.highlightedIndex >= this.hits.length - 1 ? 0 : this.highlightedIndex + 1;
      this.updateHighlightedSuggestion();
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.highlightedIndex = this.highlightedIndex <= 0 ? this.hits.length - 1 : this.highlightedIndex - 1;
      this.updateHighlightedSuggestion();
      return;
    }

    if (event.key === 'Enter' && this.highlightedIndex >= 0 && this.highlightedIndex < this.hits.length) {
      event.preventDefault();
      const selected = this.hits[this.highlightedIndex];
      const navigateTo = selected?.attributes?.cmsLink ?? selected?.url;
      if (navigateTo) {
        window.location.href = navigateTo;
      }
      return;
    }

    if (event.key === 'Escape') {
      this.closeSuggestions();
    }
  };

  private readonly onListMouseDown = (event: MouseEvent): void => {
    if (event.button !== 0) {
      return;
    }

    const target = event.target as HTMLElement;
    const seeAllItem = target.closest<HTMLElement>('[data-suggestion-see-all]');

    if (seeAllItem) {
      event.preventDefault();
      this.form.requestSubmit();
      return;
    }

    const item = target.closest<HTMLElement>('[data-suggestion-index]');

    if (!item) {
      return;
    }

    const index = Number(item.dataset.suggestionIndex ?? '-1');
    const selected = this.hits[index];

    const navigateTo = selected?.attributes?.cmsLink ?? selected?.url;

    if (!navigateTo) {
      return;
    }

    event.preventDefault();
    window.location.href = navigateTo;
  };

  private readonly onDocumentMouseDown = (event: MouseEvent): void => {
    const target = event.target as Node;

    if (!this.form.contains(target)) {
      this.closeSuggestions();
    }
  };

  private readonly onClearButtonClick = (): void => {
    this.input.value = '';
    this.clearSuggestions();
    this.updateClearButtonVisibility();
    this.input.focus();
  };

  private async fetchSuggestions(query: string): Promise<void> {
    this.activeRequest += 1;
    const requestId = this.activeRequest;

    try {
      const client = createSearchClient(this.config);
      const indexUid = this.config.index;

      if (!indexUid) {
        this.clearSuggestions();
        return;
      }

      const response = await client.index(indexUid).search(query, {
        attributesToHighlight: ['title'],
        highlightPreTag: '<b>',
        highlightPostTag: '</b>',
        rankingScoreThreshold: 0.9,
        limit: this.limit,
      });

      if (requestId !== this.activeRequest) {
        return;
      }

      const hits = (response.hits as SearchHit[]).filter((hit) => Boolean(hit?.title && (hit?.attributes?.cmsLink ?? hit?.url)));

      this.hits = hits;
      this.highlightedIndex = -1;

      if (hits.length === 0) {
        this.renderNoResultsState();
        return;
      }

      this.renderSuggestions();
    } catch {
      if (requestId !== this.activeRequest) {
        return;
      }

      this.renderErrorState();
    }
  }

  private renderErrorState(): void {
    this.hits = [];
    this.highlightedIndex = -1;
    this.openSuggestions();

    this.list.innerHTML = '';

    const error = document.createElement('div');
    error.className = 'dropdown-item-text text-danger';
    error.dataset.cy = 'cms-header-search-error';
    error.textContent = this.errorLabel;

    this.list.appendChild(error);
  }

  private renderNoResultsState(): void {
    this.hits = [];
    this.highlightedIndex = -1;
    this.openSuggestions();

    this.list.innerHTML = '';

    const noResults = document.createElement('div');
    noResults.className = 'dropdown-item-text text-secondary';
    noResults.dataset.cy = 'cms-header-search-no-results';
    noResults.textContent = this.noResultsLabel;

    this.list.appendChild(noResults);
  }

  private renderLoadingState(): void {
    this.hits = [];
    this.highlightedIndex = -1;
    this.openSuggestions();

    this.list.innerHTML = `
      <div class="dropdown-item pe-none" data-cy="cms-header-search-loading">
        <div class="ds-skeleton ds-skeleton--block" style="--ds-skeleton-height: 20px;"></div>
      </div>
    `;
  }

  private renderSuggestions(): void {
    this.openSuggestions();
    this.list.innerHTML = '';

    this.hits.forEach((hit, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dropdown-item';
      button.dataset.suggestionIndex = String(index);
      button.dataset.cy = 'cms-header-search-suggestion-item';
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', 'false');

      const wrapper = document.createElement('div');
      wrapper.className = 'd-flex align-items-center gap-2 overflow-hidden';

      let thumbnail: HTMLElement;

      if (hit.images?.dropdown) {
        const image = document.createElement('img');
        image.className = 'rounded flex-shrink-0';
        image.width = 34;
        image.height = 34;
        image.loading = 'lazy';
        image.alt = '';
        image.src = hit.images.dropdown;

        if (hit.images.dropdown2x) {
          image.srcset = `${hit.images.dropdown2x} 2x`;
        }

        thumbnail = image;
      } else {
        const placeholder = document.createElement('div');
        placeholder.className = 'rounded flex-shrink-0 bg-secondary-subtle d-flex align-items-center justify-content-center text-secondary';
        placeholder.style.width = '34px';
        placeholder.style.height = '34px';

        const icon = document.createElement('i');
        icon.className = 'ti ti-library-photo';
        placeholder.appendChild(icon);

        thumbnail = placeholder;
      }

      const textWrapper = document.createElement('div');
      textWrapper.className = 'd-flex flex-column min-w-0';

      const title = document.createElement('span');
      title.className = 'text-truncate';

      const formattedTitle = hit._formatted?.title;

      if (formattedTitle !== undefined) {
        const safeHtml = formattedTitle
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/&lt;b&gt;/gi, '<b>')
          .replace(/&lt;\/b&gt;/gi, '</b>');

        title.innerHTML = safeHtml;
      } else {
        title.textContent = hit.title ?? '';
      }

      const contentType = document.createElement('small');
      contentType.className = 'text-secondary text-uppercase';
      contentType.textContent = this.formatContentType(hit.type);

      textWrapper.append(title, contentType);
      wrapper.append(thumbnail, textWrapper);
      button.appendChild(wrapper);

      this.list.appendChild(button);
    });

    const divider = document.createElement('div');
    divider.className = 'dropdown-divider';
    this.list.appendChild(divider);

    const seeAllButton = document.createElement('button');
    seeAllButton.type = 'button';
    seeAllButton.className = 'dropdown-item text-center fw-medium';
    seeAllButton.dataset.suggestionSeeAll = 'true';
    seeAllButton.dataset.cy = 'cms-header-search-see-all-results';
    seeAllButton.textContent = this.seeAllLabel;
    this.list.appendChild(seeAllButton);
  }

  private formatContentType(contentType?: string): string {
    if (!contentType) {
      return 'Content';
    }

    return contentType
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private updateHighlightedSuggestion(): void {
    const items = this.list.querySelectorAll<HTMLElement>('[data-suggestion-index]');

    items.forEach((item, index) => {
      const isActive = index === this.highlightedIndex;
      item.classList.toggle('active', isActive);
      item.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  private clearSuggestions(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    // Invalidate any in-flight request so late responses can't reopen the dropdown.
    this.activeRequest += 1;

    this.hits = [];
    this.highlightedIndex = -1;
    this.list.innerHTML = '';
    this.closeSuggestions();
  }

  private openSuggestions(): void {
    this.list.classList.add('show');
    this.input.setAttribute('aria-expanded', 'true');
  }

  private closeSuggestions(): void {
    this.list.classList.remove('show');
    this.input.setAttribute('aria-expanded', 'false');
  }

  private updateClearButtonVisibility(): void {
    if (!this.clearButton) {
      return;
    }

    this.clearButton.hidden = this.input.value.length === 0;
  }
}
